import {
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnInit,
	Output,
	TemplateRef
} from '@angular/core';
import { SelectorElement } from "@shared/components/input/input-selector/selector-element";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";
import { ObjectAccessPipe } from "@shared/pipes/object-access.pipe";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FormsModule } from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { NgForOf, NgIf, NgTemplateOutlet } from "@angular/common";
import { MatRadioModule } from "@angular/material/radio";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatButtonModule } from "@angular/material/button";
import { SearchInputComponent } from "@shared/components/input/search-input/search-input.component";
import { SvgSelectorComponent } from "@shared/components/svg-selector/svg-selector.component";
import { CheckboxComponent } from "@shared/components/input/checkbox/checkbox.component";

export enum InputSelectorType {
	MONO = 'mono',
	MULTI = 'multi',
	DATE = 'date',
}

@Component({
	selector: 'app-input-selector',
	templateUrl: './input-selector.component.html',
	styleUrls: ['./input-selector.component.scss'],
	imports: [
		MatTooltipModule,
		FormsModule,
		TranslocoModule,
		MatCheckboxModule,
		NgTemplateOutlet,
		MatRadioModule,
		MatInputModule,
		MatDatepickerModule,
		MatButtonModule,
		NgIf,
		NgForOf,
		SearchInputComponent,
		SvgSelectorComponent,
		CheckboxComponent
	],
	standalone: true
})
export class InputSelectorComponent implements OnInit, OnChanges {
	/**
	 * @description
	 * Placeholder of the input
	 */
	@Input() placeholder: string = ' ';

	@Input() displayLabel: boolean = true

	/**
	 * @description
	 * Does the input has to include a search bar
	 */
	@Input() searchable: boolean = true;

	/**
	 * @description
	 * Current search value
	 */
	@Input() search: string = '';


	/**
	 * @description
	 * Type of the input
	 * @default InputSelectorType.MULTI
	 * @see InputSelectorType
	 */
	@Input() type: InputSelectorType = InputSelectorType.MULTI;

	/**
	 * @description
	 * Selected elements
	 */
	@Input() selected: SelectorElement[] = []
	@Output() selectedChange: EventEmitter<SelectorElement[]> = new EventEmitter<SelectorElement[]>();
	@Output() searchTrigger: EventEmitter<string> = new EventEmitter<string>();

	/**
	 * @description
	 * List of elements to display
	 */
	@Input() list: any[] = [];
	@Input() translateKey: string = '';

	/**
	 * @description
	 * Which key is used to display the value
	 */
	@Input() labelKey: string = '';

	/**
	 * @description
	 * By which key the value is accessed
	 */
	@Input() valueKey: string = '';
	@Input() right: boolean = false;
	@ContentChild(TemplateRef) templateVariable: TemplateRef<any>;

	open: boolean = false;
	searchChanged: Subject<string> = new Subject<string>();
	@Output()
	selectionChange: EventEmitter<any> = new EventEmitter();
	displayString: string = '';
	otherElement: string[] = [];

	types: typeof InputSelectorType = InputSelectorType;

	constructor(
			private eRef: ElementRef,
			private pipeAccess: ObjectAccessPipe,
			private transloco: TranslocoService
	) {
	}

	@HostListener('document:click', ['$event'])
	clickOut(event: any) {
		if (!this.eRef.nativeElement.contains(event.target)) {
			this.open = false;
		}
	}

	ngOnInit(): void {
		this.searchChanged.pipe(
				debounceTime(500),
				distinctUntilChanged())
				.subscribe(search => {
							if (this.searchTrigger.observed) {
								this.searchTrigger.emit(search);
							}
							this.search = search;
						}
				);
		this.generateDisplayString();
	}


	ngOnChanges(changes: any): void {
		if (changes.selected) {
			if (!this.searchTrigger.observed) {
				this.list.forEach(it => {
					this.updateLabel({
						label: this.label(it),
						value: this.value(it)
					});
				})
			} else {
				this.generateDisplayString();
			}
		}
	}

	changeSelected(se: SelectorElement): void {
		if (this.type) {
			const index: number = this.selected.findIndex(element => element.value === se.value)
			if (index !== -1) {
				this.selected.splice(index, 1);
			} else {
				this.selected.push(se);
			}
		} else {
			if (this.selected[0]?.value === se.value) {
				this.selected.splice(0, 1);
			} else {
				this.selected[0] = se;
			}
		}
		this.selectedChange.emit(this.selected);
		this.selectionChange.emit();
		this.generateDisplayString();
	}

	isSelected(elem: any): boolean {
		return this.selected.findIndex(element => element.value === this.value(elem)) !== -1
	}

	generateDisplayString(): void {
		const labels: string[] = [];
		this.displayString = '';
		this.otherElement = [];
		this.selected.forEach(it => labels.push(it.label!));
		if (labels.length) {
			this.displayString = labels[0];
			labels.splice(0, 1);
			if (labels.length) {
				this.otherElement = labels;
			}
		}
	}

	updateLabel(se: SelectorElement) {
		const index: number = this.selected.findIndex(element => element.value === se.value)
		if (index !== -1) {
			this.selected[index] = se;
		}
		this.generateDisplayString();
	}

	onChange(event: any) {
		this.searchChanged.next(event)
	}

	clearSelection(): void {
		this.selected = [];
		this.selectedChange.emit([])
		this.selectionChange.emit();
		this.generateDisplayString();
	}

	label(elem: any): string {
		return this.transloco.translate(this.translateKey + (this.pipeAccess.transform(elem, [this.labelKey, 'label'])))
	}

	value(elem: any): any {
		return this.pipeAccess.transform(elem, [this.valueKey, 'value'])
	}

	dateChange(event: any, elem: any) {
		elem.value = event.value;
		this.selected = [];
		this.list.forEach(it => {
			if (it.value) {
				this.selected.push(it);
			}
		})
		this.selectedChange.emit(this.selected);
		this.selectionChange.emit();
	}

	checkedChange(elem: any) {
		this.changeSelected({
			label: this.label(elem),
			value: this.value(elem)
		})
	}

	storageChange(elem: any) {
		this.changeSelected(elem);
	}

	staticSearch(elem: any): boolean {
		if (this.searchTrigger.observed) {
			return true;
		}
		return this.label(elem).toLowerCase().includes(this.search.toLowerCase());
	}


	tooltip(): string {
		let str: string = '';
		this.otherElement.forEach(it => {
			str += it + '\n';
		})
		return str;
	}
}
